import React, { useState, useEffect, useReducer, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
import "moment/locale/pt-br";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { WhatsApp } from "@material-ui/icons";
import "./Schedules.css";

function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const statusLabel = (raw) => {
  const status = String(raw || "").toUpperCase();
  if (status === "PENDENTE" || status === "PENDING") return "Agendado";
  if (status === "ENVIADA" || status === "ENVIADO" || status === "SENT") return "Enviado";
  if (status === "ERRO" || status === "ERROR" || status === "FAILED" || status === "FALHOU") return "Falhou";
  if (status === "CANCELADO" || status === "CANCELADA" || status === "CANCELED") return "Cancelado";
  if (status === "EM FILA" || status === "QUEUED") return "Em fila";
  return raw || "Não informado";
};

const statusColor = (status) => {
  const label = statusLabel(status);
  if (label === "Enviado") return { background: "#e5f8eb", color: "#128647" };
  if (label === "Falhou") return { background: "#ffe7e7", color: "#c92c37" };
  if (label === "Cancelado") return { background: "#edf0f4", color: "#64748b" };
  if (label === "Em fila") return { background: "#fff0d9", color: "#a95e0c" };
  return { background: "#e7f0ff", color: "#1762d5" };
};

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];

    schedules.forEach((schedule) => {
      const scheduleIndex = state.findIndex((s) => s.id === schedule.id);
      if (scheduleIndex !== -1) {
        state[scheduleIndex] = schedule;
      } else {
        newSchedules.push(schedule);
      }
    });

    return [...state, ...newSchedules];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule;
      return [...state];
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;

    const scheduleIndex = state.findIndex((s) => s.id === scheduleId);
    if (scheduleIndex !== -1) {
      state.splice(scheduleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  page: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflowY: "auto", padding: "8px 3px 20px", ...theme.scrollbarStyles },
  titleRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 4 },
  title: { margin: 0, color: "#15254a", fontSize: 29, fontWeight: 800 },
  subtitle: { color: "#7c879d", margin: "0 0 18px" },
  counter: { borderRadius: 30, padding: "6px 15px", background: "#e9f1ff", color: "#245bd6", fontWeight: 700 },
  card: { padding: 18, background: "#fff", border: "1px solid #eaf0f9", borderRadius: 20, boxShadow: "0 8px 32px rgba(18, 49, 99, 0.045)" },
  toolbar: { display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap", '& .MuiOutlinedInput-root': { borderRadius: 11 }, '& .MuiOutlinedInput-input': { padding: '12px 13px' } },
  search: { flex: "1 1 245px", minWidth: 160 },
  filter: { flex: "0 1 155px", minWidth: 115 },
  add: { minHeight: 43, borderRadius: 10, textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" },
  summary: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10, marginBottom: 16, [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2,minmax(0,1fr))" } },
  summaryCell: { textAlign: "center", padding: "12px 8px", borderRadius: 11, fontWeight: 700 },
  scroller: { overflowX: "auto", width: "100%", border: "1px solid #eaf0f8", borderRadius: 13 },
  table: { width: "100%", minWidth: 1010, borderCollapse: "collapse", fontSize: 13, color: "#25344f", '& th': { background: "#f6f9fe", textAlign: "left", padding: "13px 10px", whiteSpace: "nowrap", borderBottom: "1px solid #e7edf7" }, '& td': { padding: "9px 10px", borderBottom: "1px solid #eff2f7" }, '& tr:hover td': { background: "#fafdff" } },
  preview: { maxWidth: 230, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" },
  foot: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginTop: 14, color: "#7b869b", fontSize: 13 },
}));

const Schedules = () => {
  const classes = useStyles();
  const history = useHistory();

  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);


  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));
  const [statusFilter, setStatusFilter] = useState("all");
  const [connectionFilter, setConnectionFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useSchedules) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
  }, [user, history, getPlanCompany]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId,
  ]);

  useEffect(() => {
    // handleOpenScheduleModalFromContactId();
    // const socket = socketManager.GetSocket(user.companyId, user.id);


    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    }

    socket.on(`company${user.companyId}-schedule`, onCompanySchedule)

    return () => {
      socket.off(`company${user.companyId}-schedule`, onCompanySchedule)
    };
  }, [socket]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const connectionOptions = Array.from(new Map(schedules.filter(s => s.whatsapp).map(s => [String(s.whatsapp.id), s.whatsapp.name])).entries());
  const visibleSchedules = schedules.filter(schedule => {
    if (statusFilter !== "all" && statusLabel(schedule.status) !== statusFilter) return false;
    if (connectionFilter !== "all" && String(schedule.whatsappId || (schedule.whatsapp && schedule.whatsapp.id)) !== connectionFilter) return false;
    if (dateFilter && moment(schedule.sendAt).format("YYYY-MM-DD") !== dateFilter) return false;
    return true;
  }).sort((a, b) => {
    const rank = { "Agendado": 0, "Em fila": 1, "Enviado": 2, "Falhou": 3, "Cancelado": 4 };
    const aRank = rank[statusLabel(a.status)] === undefined ? 5 : rank[statusLabel(a.status)];
    const bRank = rank[statusLabel(b.status)] === undefined ? 5 : rank[statusLabel(b.status)];
    if (aRank !== bRank) return aRank - bRank;
    const aTime = new Date(a.sendAt).getTime();
    const bTime = new Date(b.sendAt).getTime();
    const byTime = aRank <= 1 ? aTime - bTime : bTime - aTime;
    return byTime || Number(b.id) - Number(a.id);
  });
  const counts = { "Agendado": 0, "Enviado": 0, "Falhou": 0, "Cancelado": 0 };
  schedules.forEach(schedule => {
    const label = statusLabel(schedule.status);
    if (Object.prototype.hasOwnProperty.call(counts, label)) counts[label] += 1;
  });

  return (
    <MainContainer>
      <div className={classes.page}>
        <ConfirmationModal
          title={deletingSchedule && i18n.t("schedules.confirmationModal.deleteTitle")}
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={() => deletingSchedule && handleDeleteSchedule(deletingSchedule.id)}
        >
          {i18n.t("schedules.confirmationModal.deleteMessage")}
        </ConfirmationModal>
        {scheduleModalOpen && (
          <ScheduleModal
            open={scheduleModalOpen}
            onClose={handleCloseScheduleModal}
            reload={fetchSchedules}
            scheduleId={selectedSchedule ? selectedSchedule.id : null}
            contactId={contactId}
            cleanContact={cleanContact}
          />
        )}
        <div className={classes.titleRow}>
          <h1 className={classes.title}>{i18n.t("schedules.title")}</h1>
          <span className={classes.counter}>{schedules.length}{hasMore ? "+" : ""}</span>
        </div>
        <p className={classes.subtitle}>Visualize e gerencie seus agendamentos de mensagens.</p>
        <Paper elevation={0} className={classes.card}>
          <div className={classes.toolbar}>
            <TextField className={classes.search} variant="outlined" placeholder="Pesquisar..." value={searchParam} onChange={handleSearch}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="primary" /></InputAdornment> }} />
            <TextField select variant="outlined" className={classes.filter} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <MenuItem value="all">Todos os status</MenuItem>
              {Array.from(new Set(schedules.map(s => statusLabel(s.status)))).map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
            <TextField select variant="outlined" className={classes.filter} value={connectionFilter} onChange={e => setConnectionFilter(e.target.value)}>
              <MenuItem value="all">Todas as conexões</MenuItem>
              {connectionOptions.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
            </TextField>
            <TextField type="date" variant="outlined" className={classes.filter} value={dateFilter} onChange={e => setDateFilter(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button className={classes.add} variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenScheduleModal}>
              {i18n.t("schedules.buttons.add")}
            </Button>
          </div>
          <div className={classes.summary}>
            {[
              ["Agendado", "Agendados", "#e9f3ff", "#185bd0"],
              ["Enviado", "Enviados", "#eaf8ef", "#119052"],
              ["Falhou", "Falharam", "#fff0f0", "#da3741"],
              ["Cancelado", "Cancelados", "#f1f3f7", "#59647b"]
            ].map(([key, label, bg, color]) => (
              <div key={key} className={classes.summaryCell} style={{ background: bg, color }}>
                <span style={{ fontSize: 19 }}>{counts[key]}</span><div style={{ fontWeight: 500 }}>{label}</div>
              </div>
            ))}
          </div>
          <div className={classes.scroller}>
            <table className={classes.table}>
              <thead><tr>
                <th>Contato</th><th>Número</th><th>Data</th><th>Hora</th><th>Conexão</th><th>Mensagem / Campanha</th><th>Status</th><th>Ações</th>
              </tr></thead>
              <tbody>
                {visibleSchedules.map(schedule => (
                  <tr key={schedule.id}>
                    <td><div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Avatar style={{ width: 30, height: 30 }} src={schedule.contact && schedule.contact.urlPicture || "/nopicture.png"} />
                      <strong>{schedule.contact && schedule.contact.name || "Contato não disponível"}</strong>
                    </div></td>
                    <td style={{ color: "#52627c" }}>{schedule.contact && schedule.contact.number || "—"}</td>
                    <td>{moment(schedule.sendAt).format("DD/MM/YYYY")}</td>
                    <td>{moment(schedule.sendAt).format("HH:mm")}</td>
                    <td>{schedule.whatsapp ? <span style={{ color: "#128647", whiteSpace: "nowrap" }}><WhatsApp style={{ fontSize: 16, verticalAlign: "middle" }} /> {schedule.whatsapp.name}</span> : "—"}</td>
                    <td><Tooltip title={schedule.body || ""}><div className={classes.preview}>{schedule.body || (schedule.mediaName ? `[Mídia] ${schedule.mediaName}` : "—")}</div></Tooltip></td>
                    <td><Chip size="small" label={statusLabel(schedule.status)} style={{ ...statusColor(schedule.status), fontWeight: 700, minWidth: 85 }} /></td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Visualizar mensagem"><IconButton size="small" onClick={() => { setSelectedSchedule(schedule); setScheduleModalOpen(true); }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Editar agendamento"><IconButton size="small" onClick={() => handleEditSchedule(schedule)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir agendamento"><IconButton size="small" onClick={() => { setDeletingSchedule(schedule); setConfirmModalOpen(true); }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                    </td>
                  </tr>
                ))}
                {!loading && visibleSchedules.length === 0 && <tr><td colSpan="8" style={{ textAlign: "center", padding: 32 }}>Nenhum agendamento encontrado.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className={classes.foot}>
            <span>Mostrando {visibleSchedules.length} de {schedules.length}{hasMore ? "+" : ""} agendamentos carregados. Filtros e resumos consideram os registros carregados.</span>
            {hasMore && <Button variant="outlined" color="primary" onClick={loadMore} disabled={loading}>{loading ? "Carregando..." : "Carregar mais"}</Button>}
          </div>
        </Paper>
      </div>
    </MainContainer>
  );
};

export default Schedules;
